import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  DEFAULT_TIME_SERIES_ALLOW_EDIT_SUBMITTED_ROWS,
  DEFAULT_TIME_SERIES_DUPLICATE_HANDLING,
  DEFAULT_TIME_SERIES_PERIOD,
  DEFAULT_TIME_SERIES_TIMEZONE,
  DEFAULT_TIME_SERIES_TIMESTAMP_FIELD,
  TIME_SERIES_DUPLICATE_HANDLING,
  TimeSeriesDuplicateHandling,
  TimeSeriesPeriod,
} from '@anessa/shared-models';
import { I18nPipe } from '../../../../shared/i18n/i18n-pipe';
import { I18nScopeReadyDirective } from '../../../../shared/i18n/i18n-scope-ready.directive';
import { provideI18nScope } from '../../../../shared/i18n/i18n-scope.provider';
import { I18nScopedService } from '../../../../shared/i18n/i18n-scoped.service';
import { ConfirmationDialog } from '../../../../shared/common/confirmation-dialog/confirmation-dialog';

export interface EditTimeSeriesTableDialogData {
  id: string;
  name: string;
  description: string;
  timezone: string;
  timestampField: string;
  duplicateHandling: TimeSeriesDuplicateHandling;
  period: TimeSeriesPeriod;
  allowEditSubmittedRows: boolean;
}

export interface EditTimeSeriesTableSaveResult {
  action: 'save';
  name: string;
  description: string;
  timezone: string;
  timestampField: string;
  duplicateHandling: TimeSeriesDuplicateHandling;
  period: TimeSeriesPeriod;
  allowEditSubmittedRows: boolean;
}

export interface EditTimeSeriesTableDeleteResult {
  action: 'delete';
  id: string;
}

export type EditTimeSeriesTableResult =
  | EditTimeSeriesTableSaveResult
  | EditTimeSeriesTableDeleteResult;

@Component({
  selector: 'app-edit-timeseries-table-dialog',
  imports: [
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatButtonModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTooltipModule,
    I18nPipe,
    I18nScopeReadyDirective,
  ],
  providers: [
    provideI18nScope('features/timeseries-data/edit-table-dialog'),
    I18nScopedService,
  ],
  templateUrl: './edit-timeseries-table-dialog.html',
  styleUrl: './edit-timeseries-table-dialog.scss',
})
export class EditTimeSeriesTableDialog {
  private readonly dialogRef = inject(MatDialogRef<EditTimeSeriesTableDialog>);
  private readonly dialog = inject(MatDialog);

  readonly data = inject(MAT_DIALOG_DATA) as EditTimeSeriesTableDialogData;

  name = this.data.name;
  description = this.data.description;
  timezone = this.data.timezone || DEFAULT_TIME_SERIES_TIMEZONE;
  timestampField = this.data.timestampField;
  duplicateHandling =
    this.data.duplicateHandling ?? DEFAULT_TIME_SERIES_DUPLICATE_HANDLING;
  period = this.data.period;
  allowEditSubmittedRows =
    this.data.allowEditSubmittedRows ??
    DEFAULT_TIME_SERIES_ALLOW_EDIT_SUBMITTED_ROWS;
  readonly duplicateHandlingOptions = TIME_SERIES_DUPLICATE_HANDLING;
  readonly periodOptions: TimeSeriesPeriod[] = ['any', 'day'];

  get isValid(): boolean {
    return this.name.trim().length > 0;
  }

  apply(): void {
    if (!this.isValid) return;
    this.dialogRef.close({
      action: 'save',
      name: this.name.trim(),
      description: this.description.trim(),
      timezone: this.timezone || DEFAULT_TIME_SERIES_TIMEZONE,
      timestampField:
        this.timestampField.trim() || DEFAULT_TIME_SERIES_TIMESTAMP_FIELD,
      duplicateHandling: this.duplicateHandling,
      period: this.period || DEFAULT_TIME_SERIES_PERIOD,
      allowEditSubmittedRows: this.allowEditSubmittedRows,
    } satisfies EditTimeSeriesTableResult);
  }

  requestDelete(): void {
    this.dialog
      .open(ConfirmationDialog, {
        width: '400px',
        data: {
          confirmButtonType: 'delete',
        },
      })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.dialogRef.close({
          action: 'delete',
          id: this.data.id,
        } satisfies EditTimeSeriesTableResult);
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
